import {
	GROUNDING_FIELDS,
	GROUNDING_HINTS,
	RETIRED_GROUNDING_HINTS,
	RETIRED_LABEL_SUCCESSORS,
	SECTION_HEADINGS,
	SEED_INTRO,
	type GroundingAnswers,
	type GroundingCorrectionId,
	type GroundingFieldId,
	type GroundingSection,
} from './groundingFields';

// Markdown <-> answers for the grounding brief. Markdown stays the persisted shape (one string
// field in Cosmos, read as opaque prose by the strategist), so this module is the whole contract:
// the guided flow never sees markdown and the backend never sees answers.
//
// Everything here is defensive about ONE fact: the doc may have been written by a human through
// the old free-prose TipTap editor, not by our renderer. Unrecognized content is preserved
// verbatim in `extra` and re-emitted, because the first save is irreversible — it flips the
// server's `edited` flag, after which the seed is never re-rendered and there is no history.

const SECTION_ORDER: readonly GroundingSection[] = ['today', 'goal', 'plan'];

const LABEL_LINE = /^-\s+\*\*(.+?):\*\*\s*(.*)$/;

export interface ParsedGrounding {
	answers: GroundingAnswers;
	/** Content we did not recognize, kept verbatim so a hand-written brief is never destroyed. */
	extra: string;
	/** True when the doc had no recognizable labels at all — a fully hand-written brief. */
	unstructured: boolean;
}

const byLabel = new Map(GROUNDING_FIELDS.map((f) => [f.mdLabel, f.id]));

// Maps, not the object literals. An owner's bullet supplies the key, and indexing an object with
// it reaches `Object.prototype` — a bullet labelled `constructor` resolved to a function, was
// stored under it, and then vanished, because compose emits GROUNDING_FIELDS only.
const retiredSuccessors = new Map(Object.entries(RETIRED_LABEL_SUCCESSORS));
const retiredHints = new Map(Object.entries(RETIRED_GROUNDING_HINTS));

// The same hints, keyed by the question that REPLACED the retired one. A correction is stored
// under a field id and never under the old label, so the label-keyed map above cannot reach one.
// Only a label with a successor appears here: without one, no field's answer can hold its hint.
//
// Keying by OUR field id also makes the value-alone judgement safe here, which it is not during
// parsing. The owner supplies a label in the brief, so a value-alone test there deletes their own
// bullet whenever its words match a hint. A correction key is ours, so there is nothing to misjudge.
const retiredHintsByField = new Map<GroundingCorrectionId, readonly string[]>();
for (const [label, field] of retiredSuccessors) {
	const hints = retiredHints.get(label) ?? [];
	if (hints.length) {
		retiredHintsByField.set(field, [
			...(retiredHintsByField.get(field) ?? []),
			...hints,
		]);
	}
}

// The remainder once one of `hints` is removed from `bare`, or `null` when `bare` holds none of
// them and must stay byte-identical. `''` means the hint was the whole value.
//
// A partial strip needs the emphasis delimiters. Undelimited, only a whole-value match is safe:
// a prefix or suffix match on plain prose would eat a sentence the owner wrote.
//
// Both ends are checked. The seed writes a retired hint ALONE, so an owner who typed their answer
// next to the pre-filled hint — rather than clearing it — saved our coaching question at the
// START of their answer. `corrections` are merged server-side and outrank the brief, so that one
// save is permanent: no later parse sees the hint at the end, and the owner has no history to
// undo it. That is the ADO-1983 failure, for the owners this branch is repairing.
const withoutOneOf = (
	hints: readonly string[],
	bare: string,
): string | null => {
	for (const hint of hints) {
		if (bare === hint) return '';
		for (const delimiter of ['_', '*']) {
			const wrapped = `${delimiter}${hint}${delimiter}`;
			if (bare === wrapped) return '';
			if (bare.endsWith(wrapped)) return bare.slice(0, -wrapped.length).trim();
			if (bare.startsWith(wrapped)) return bare.slice(wrapped.length).trim();
		}
	}
	return null;
};

// A brief saved before a question was retired still carries that question's label. Route it to
// the question that replaced it, so the owner's answer lands against something they can see and
// edit rather than becoming unplaceable text.
const labelToField = (label: string): GroundingFieldId | undefined =>
	byLabel.get(label) ?? retiredSuccessors.get(label);

// TipTap escapes markdown punctuation in plain text; undo that before matching hint literals.
const unescapeMarkdown = (s: string): string =>
	s.replace(/\\([\\`*_{}[\]()#+\-.!~])/g, '$1');

// Strip a trailing emphasis run only when it IS one of the seed's known coaching hints. The seed
// writes `_..._`; TipTap re-serializes the same mark as `*...*` (prosemirror-markdown's em mark),
// so both delimiters have to be considered. Anything else is the owner's own emphasis and stays.
const stripKnownHint = (value: string): string => {
	const trimmed = value.trimEnd();
	for (const delimiter of ['_', '*']) {
		for (const hint of GROUNDING_HINTS) {
			const suffix = `${delimiter}${hint}${delimiter}`;
			if (trimmed.endsWith(suffix)) {
				return trimmed.slice(0, -suffix.length).trim();
			}
		}
	}
	return trimmed;
};

// What a retired bullet holds once the coaching hint the seed wrote under THAT label is removed:
// `''` when the hint was all of it, a shorter string when it trailed real text, and `null` when
// the bullet carries no hint of ours and must stay byte-identical.
//
// It returns the remainder rather than a boolean because the seed wrote one retired bullet in two
// shapes. `Steering` carried the hint alone, and — for a business with brand-voice research — the
// researched voice AND the hint on one line. A whole-value test sees only the first, so the second
// kept our coaching question and handed it to the strategist as the owner's own instruction.
//
// The label is half the test, and it has to be. Judged on the value alone, an owner's own bullet
// under a label they invented is deleted whenever its words happen to match a hint, which breaks
// the preserve-verbatim contract above on a save that has no history to undo it.
const withoutThisLabelsHint = (label: string, value: string): string | null =>
	withoutOneOf(retiredHints.get(label) ?? [], unescapeMarkdown(value).trim());

/**
 * Reduce a stored answer to what the owner actually said.
 *
 * The brief is parsed through `stripKnownHint`, but `corrections` are persisted separately, are
 * merged server-side rather than replaced, and outrank the brief on load. A hint written there
 * before it was strippable therefore survives every later fix. Run every inbound answer through
 * this — on read and on write — so no path can reintroduce one (ADO-1983).
 */
export const stripHintFromAnswer = (
	value: string,
	field?: GroundingCorrectionId,
): string => {
	const bare = unescapeMarkdown(value).trim();
	// A RETIRED question's hint is deliberately absent from GROUNDING_HINTS, so `stripKnownHint`
	// below can never see one. Without this, a correction saved from a retired bullet outranked
	// the brief on every later load and no path could remove it — the ADO-1983 failure again, for
	// the one label whose answer moved to a live question. Pass the field id to reach it.
	const retired =
		field === undefined
			? null
			: withoutOneOf(retiredHintsByField.get(field) ?? [], bare);
	if (retired !== null) return retired;
	const stripped = stripKnownHint(bare);
	// A hint copied back without its emphasis leaves the delimiter check nothing to match. Only a
	// whole-value match is safe here: a suffix match on undelimited prose would eat an owner's own
	// closing sentence.
	return GROUNDING_HINTS.includes(stripped) ? '' : stripped;
};

const isSeedChrome = (line: string): boolean => {
	const t = line.trim();
	if (t.startsWith('# Grounding:')) return true;
	if (t === SEED_INTRO) return true;
	return SECTION_ORDER.some((s) => t === `## ${SECTION_HEADINGS[s]}`);
};

/**
 * Read a stored brief back into per-question answers.
 *
 * A value may span several lines: TipTap serializes a hard break as a trailing backslash, and the
 * flow itself offers Shift+Enter on long fields. Continuation lines are folded into the preceding
 * answer rather than orphaned into `extra`, which would re-compose them out of order at the end.
 */
export const parseGroundingMarkdown = (markdown: string): ParsedGrounding => {
	const answers: GroundingAnswers = {};
	const extraLines: string[] = [];
	const seen = new Set<GroundingFieldId>();
	const lines = (markdown ?? '').split('\n');

	let current: GroundingFieldId | null = null;
	let currentLabel = '';
	let buffer: string[] = [];

	const flush = () => {
		if (!current) return;
		// Every line may carry TipTap's trailing hard-break backslash, including the label line.
		const joined = buffer.map((l) => l.replace(/\\$/, '')).join('\n');
		// A retired bullet that held the owner's answer AND our hint on one line reaches here with
		// both, because `ourProse` above is false for it and the raw value is buffered. Its hint is
		// keyed by the RETIRED label, which `stripKnownHint` does not hold, so the strip below
		// cannot see it and stored our coaching question as the owner's answer. Run the label-keyed
		// strip here, where the continuation lines are already folded in.
		const retired = withoutThisLabelsHint(currentLabel, joined);
		const value = retired ?? stripKnownHint(unescapeMarkdown(joined).trim());
		if (value) answers[current] = value;
		current = null;
		currentLabel = '';
		buffer = [];
	};

	for (const line of lines) {
		const match = LABEL_LINE.exec(line);
		if (match) {
			flush();
			const label = match[1].trim();
			const id = labelToField(label);
			// Our own coaching prose, not the owner's answer, from either of two sources: the hint
			// the seed wrote under this exact retired label, or — for a label we still own — any
			// value the live hint strip empties. The retired half is keyed by label on purpose.
			// Judged on the value alone it deletes an owner's bullet under a label they invented
			// whenever its words happen to match one of ours.
			//
			// `match[2]` may carry TipTap's hard-break backslash. `flush` strips it later, so a
			// test against the raw capture misses the hint and stores it as the answer.
			const value = match[2].replace(/\\$/, '');
			const kept = withoutThisLabelsHint(label, value);
			const ourProse =
				kept === '' ||
				(id !== undefined &&
					stripKnownHint(unescapeMarkdown(value).trim()) === '');
			// A repeated label is the owner's own writing, not our bullet — keep it verbatim
			// rather than letting the second occurrence overwrite (or silently vanish).
			if (id && !seen.has(id)) {
				seen.add(id);
				current = id;
				currentLabel = label;
				// Emptied rather than skipped. A hard-break continuation below still answers this
				// question, and skipping the line would orphan the owner's real answer into
				// `extra`, where no question shows it and they are asked for it again.
				buffer = [ourProse ? '' : match[2]];
			} else if (!ourProse) {
				// `extra` is never hint-stripped later, and it is re-emitted into the brief the
				// strategist reads. So drop our coaching question HERE, and keep everything it
				// trailed. `null` means the bullet holds none of ours, so the line stays as-is.
				extraLines.push(kept === null ? line : `- **${label}:** ${kept}`);
			}
			continue;
		}

		const isBlank = line.trim() === '';
		const isHeading = line.trimStart().startsWith('#');

		if (current) {
			if (isBlank || isHeading) {
				flush();
				if (isHeading && !isSeedChrome(line)) extraLines.push(line);
				continue;
			}
			// A continuation of the value above (TipTap hard break, or a wrapped line).
			buffer.push(line.replace(/\\$/, ''));
			continue;
		}

		if (!isSeedChrome(line)) extraLines.push(line);
	}
	flush();

	return {
		answers,
		extra: extraLines.join('\n').trim(),
		unstructured: seen.size === 0,
	};
};

export interface ComposeArgs {
	answers: GroundingAnswers;
	label: string;
	extra?: string;
	/**
	 * The doc was hand-written prose with no recognizable structure. Its full text rides in
	 * `extra` and is emitted FIRST, with any answers appended below — never replaced.
	 */
	preserveOriginalFirst?: boolean;
	maxChars?: number;
}

const DEFAULT_MAX_CHARS = 40_000;

/**
 * Render answers back to the brief. Only answered fields are emitted, and a coaching hint is
 * never written — a partially completed brief must read as a shorter honest brief, never as our
 * own template prose attributed to the owner.
 */
export const composeGroundingMarkdown = ({
	answers,
	label,
	extra = '',
	preserveOriginalFirst = false,
	maxChars = DEFAULT_MAX_CHARS,
}: ComposeArgs): string => {
	const answered = GROUNDING_FIELDS.filter((f) => (answers[f.id] ?? '').trim());
	const blocks: string[] = [];

	if (answered.length) {
		blocks.push(`# Grounding: ${label}`, '', SEED_INTRO, '');
		for (const section of SECTION_ORDER) {
			const fields = answered.filter((f) => f.section === section);
			if (!fields.length) continue;
			blocks.push(`## ${SECTION_HEADINGS[section]}`, '');
			for (const f of fields) {
				blocks.push(`- **${f.mdLabel}:** ${(answers[f.id] ?? '').trim()}`);
			}
			blocks.push('');
		}
	}

	const composed = blocks.join('\n').trimEnd();
	const trimmedExtra = extra.trim();
	if (!trimmedExtra) return composed;

	const joined = preserveOriginalFirst
		? [trimmedExtra, composed].filter(Boolean).join('\n\n')
		: [composed, trimmedExtra].filter(Boolean).join('\n\n');

	// The server rejects an over-cap body outright, so trim the preserved block rather than let
	// the whole save fail — a live answer is never the thing that gets dropped.
	if (joined.length <= maxChars) return joined;
	const room = maxChars - composed.length - 2;
	if (room <= 0) return composed.slice(0, maxChars);
	const clipped = trimmedExtra.slice(0, room);
	return preserveOriginalFirst
		? [clipped, composed].filter(Boolean).join('\n\n')
		: [composed, clipped].filter(Boolean).join('\n\n');
};
